import type { CoverageViewModel } from 'src/api/types/CoverageViewModel';

import { useParams } from 'react-router';

import Grid from '@mui/material/Grid';
import { Card, Typography, CardContent } from '@mui/material';

import { MESSAGES } from 'src/constants/messages';
import { useCoverageTimelineById } from 'src/api/hooks/useCoverageDataQueries';

import { useSupportDrawerContext } from 'src/components/support-drawer';

import PolicyList from './coverage-policies/policyList';
import CoverageProfile from './coverage-profile/coverage-profile';
import SharedTimeline from '../../shared/components/shared-timeline';
import { ScrollableBox } from '../../shared/components/scrollable-box';
import AssignedRiskApplicationEntry from '../../assigned-risk-application/components/assigned-risk-application-entry';

type CoverageGeneralProps = {
  coverageSummary: CoverageViewModel;
};

export function CoverageGeneral({ coverageSummary }: CoverageGeneralProps) {
  const drawerContext = useSupportDrawerContext();

  let headerWidth: string = '100%';
  if (drawerContext.openDrawer) {
    if (drawerContext.drawerWidth.includes('%')) {
      const numPart: string = drawerContext.drawerWidth.replace('%', '');
      const calcDrawer: number = Number.parseFloat(numPart) / 100;
      headerWidth = `calc(100% - (100vw * ${calcDrawer.toString()}))`;
    } else headerWidth = `calc(100% - ${drawerContext.drawerWidth})`;
  }

  const { comboId, coverageId } = useParams<{
    comboId: string;
    coverageId: string;
  }>();

  const { data } = useCoverageTimelineById(Number(comboId), Number(coverageId));

  // Determine the events to display
  const timelineEvents = data?.data?.timelineEvents || [];
  const assignedRiskApplications = coverageSummary.assignedRiskApplications ?? [];

  const renderContent = () => (
    <>
      {/* Coverage Profile */}
      <Grid container spacing={2} columns={12}>
        {/* Policy Information Block */}
        <Grid size={{ xs: 12, md: drawerContext.openDrawer ? 12 : 8 }}>
          <Card
            sx={[
              () => ({
                display: 'flex',
                zIndex: 'unset',
                overflow: 'unset',
                mt: 5,
                borderRadius: 2,
                boxShadow: 'var(--customShadows-card)',
                width: headerWidth,
              }),
            ]}
          >
            <CoverageProfile coverageSummary={coverageSummary} />
          </Card>
          {assignedRiskApplications.length > 0 && comboId && coverageId ? (
            <Card
              sx={{
                mt: 3,
                borderRadius: 2,
                boxShadow: 'var(--customShadows-card)',
              }}
              data-testid="assigned-risk-applications-section"
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" component="h2" sx={{ mb: 1.5 }}>
                  {MESSAGES.ASSIGNED_RISK_APPLICATION.APPLICATIONS_SECTION_TITLE}
                </Typography>
                <ScrollableBox
                  data-testid="assigned-risk-applications-scroll"
                  maxHeight="46rem"
                  padding={1.5}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    marginBottom: 0,
                    '& > [data-testid="assigned-risk-application-card"], & > * > [data-testid="assigned-risk-application-card"]': {
                      mt: 0,
                    },
                  }}
                >
                  {assignedRiskApplications.map((application, index) => (
                    <AssignedRiskApplicationEntry
                      key={application.assignedRiskAppId ?? `application-${index}-${application.applicationName ?? 'unknown'}`}
                      application={application}
                      comboId={comboId}
                      coverageId={coverageId}
                    />
                  ))}
                </ScrollableBox>
              </CardContent>
            </Card>
          ) : null}
          {/* Policies */}
          <Card
            sx={[
              () => ({
                display: 'flex',
                zIndex: 'unset',
                overflow: 'unset',
                mt: 3,
                borderRadius: 2,
                boxShadow: 'var(--customShadows-card)',
                width: headerWidth,
              }),
            ]}
          >
            <PolicyList coverageSummary={coverageSummary ?? []} />
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: drawerContext.openDrawer ? 12 : 4 }}>
          <Card
            sx={[
              () => ({
                zIndex: 'unset',
                overflow: 'unset',
                mt: drawerContext.openDrawer ? 0 : 5,
                borderRadius: 2,
                boxShadow: 'var(--customShadows-card)',
                width: 'fit-content', // Allow the width to adjust dynamically
                maxWidth: headerWidth, // Ensure it doesn't exceed the calculated header width
              }),
            ]}
          >
            <SharedTimeline title="Coverage timeline" timelineEvents={timelineEvents} />
          </Card>
        </Grid>
      </Grid>
    </>
  );

  return <>{renderContent()}</>;
}
